import React, { useEffect, useState } from "react";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  BlockDes,
  Icon,
  Button,
  DataTable,
  DataTableBody,
  DataTableHead,
  DataTableRow,
  DataTableItem,
  PaginationComponent,
} from "../../../components/Component";
import { Modal, ModalBody, Form, Spinner } from "reactstrap";
import { useForm } from "react-hook-form";
import { api } from "../../../utils/api";
import { toast } from "react-toastify";
import PermissionGate from "../../../components/permissions/PermissionGate";

const paginatedRows = (res) => (Array.isArray(res?.data) ? res.data : []);

const HseProcessAssetsPage = () => {
  const [assets, setAssets] = useState([]);
  const [types, setTypes] = useState([]);
  const [sites, setSites] = useState([]);
  const [riskZones, setRiskZones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(false);
  const [typeModal, setTypeModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [safetyHistory, setSafetyHistory] = useState(null);
  const [itemPerPage] = useState(10);
  const [listSearch, setListSearch] = useState("");
  const [listPage, setListPage] = useState(1);

  const { register, handleSubmit, reset } = useForm();
  const typeForm = useForm();

  const load = async () => {
    setLoading(true);
    try {
      const [aRes, tRes] = await Promise.all([
        api.getAssets({ per_page: 200 }),
        api.getAssetTypes(),
      ]);
      setAssets(paginatedRows(aRes));
      setTypes(Array.isArray(tRes) ? tRes : []);
    } catch (e) {
      toast.error(e.message || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    (async () => {
      try {
        const [sRes, rzRes] = await Promise.all([
          api.getSites(),
          api.getRiskZones({ per_page: 200 }).catch(() => ({ data: [] })),
        ]);
        setSites(sRes.data || []);
        setRiskZones(paginatedRows(rzRes));
      } catch (e) {
        /* ignore */
      }
    })();
  }, []);

  useEffect(() => {
    setListPage(1);
  }, [listSearch]);

  const norm = (v) => String(v ?? "").toLowerCase();
  const filteredAssets = assets.filter((a) => {
    if (!listSearch.trim()) return true;
    const q = norm(listSearch);
    const score = a.latest_risk_score?.score ?? a.latestRiskScore?.score;
    return (
      norm(a.name).includes(q) ||
      norm(a.identifier).includes(q) ||
      norm(a.site?.name).includes(q) ||
      norm(a.asset_type?.name).includes(q) ||
      String(score ?? "").includes(listSearch.trim()) ||
      String(a.id).includes(listSearch.trim())
    );
  });
  const listLast = listPage * itemPerPage;
  const listFirst = listLast - itemPerPage;
  const pageAssets = filteredAssets.slice(listFirst, listLast);

  useEffect(() => {
    if (!detail?.id) {
      setSafetyHistory(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const h = await api.getAssetSafetyHistory(detail.id);
        if (!cancelled) setSafetyHistory(h);
      } catch {
        if (!cancelled) setSafetyHistory(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [detail?.id]);

  const openNew = () => {
    setEditId(null);
    reset({
      site_id: "",
      asset_type_id: "",
      name: "",
      identifier: "",
      maintenance_safety_flag: false,
    });
    setModal(true);
  };

  const openEdit = (a) => {
    setEditId(a.id);
    reset({
      site_id: String(a.site_id),
      asset_type_id: String(a.asset_type_id),
      name: a.name,
      identifier: a.identifier || "",
      maintenance_safety_flag: !!a.maintenance_safety_flag,
    });
    setModal(true);
  };

  const onSave = async (data) => {
    const maintenance =
      data.maintenance_safety_flag === true ||
      data.maintenance_safety_flag === "true" ||
      data.maintenance_safety_flag === "on";
    try {
      if (editId) {
        await api.updateAsset(editId, {
          name: data.name,
          identifier: data.identifier || null,
          maintenance_safety_flag: maintenance,
        });
        toast.success("Actif mis à jour");
      } else {
        await api.createAsset({
          site_id: parseInt(data.site_id, 10),
          asset_type_id: parseInt(data.asset_type_id, 10),
          name: data.name,
          identifier: data.identifier || null,
          maintenance_safety_flag: maintenance,
        });
        toast.success("Actif créé");
      }
      setModal(false);
      load();
    } catch (e) {
      const msg = e.errors ? Object.values(e.errors).flat().join(", ") : e.message;
      toast.error(msg || "Erreur");
    }
  };

  const onDelete = async () => {
    if (!deleteId) return;
    try {
      await api.deleteAsset(deleteId);
      toast.success("Actif supprimé");
      setDeleteModal(false);
      setDeleteId(null);
      load();
    } catch (e) {
      toast.error(e.message || "Erreur");
    }
  };

  const openDetail = async (a) => {
    try {
      const full = await api.getAsset(a.id);
      setDetail(full);
    } catch (e) {
      toast.error(e.message || "Erreur");
    }
  };

  const onNewType = async (d) => {
    try {
      await api.createAssetType({ name: d.type_name, slug: d.type_slug || undefined });
      toast.success("Type créé");
      setTypeModal(false);
      typeForm.reset({ type_name: "", type_slug: "" });
      const tRes = await api.getAssetTypes();
      setTypes(Array.isArray(tRes) ? tRes : []);
    } catch (e) {
      toast.error(e.message || "Erreur");
    }
  };

  return (
    <React.Fragment>
      <Head title="Actifs HSE" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page>Actifs & sécurité</BlockTitle>
              <BlockDes className="text-soft">
                Registre des équipements, liaison inspections / incidents / zones, historique de sécurité et score de risque
                agrégé.
              </BlockDes>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          <BlockBetween className="mb-3">
            <BlockHeadContent>
              <div className="form-control-wrap" style={{ minWidth: 260 }}>
                <div className="form-icon form-icon-left">
                  <Icon name="search" />
                </div>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Rechercher un actif…"
                  value={listSearch}
                  onChange={(e) => setListSearch(e.target.value)}
                />
              </div>
              {!loading && (
                <BlockDes className="text-soft mt-1 mb-0">
                  <p className="small mb-0">
                    {listSearch
                      ? `${filteredAssets.length} actif(s) sur ${assets.length}`
                      : `${assets.length} actif(s)`}
                  </p>
                </BlockDes>
              )}
            </BlockHeadContent>
            <BlockHeadContent>
              <ul className="nk-block-tools g-3">
                <PermissionGate permission={["Actifs - Créer"]}>
                  <li>
                    <Button color="light" size="md" onClick={() => setTypeModal(true)}>
                      Nouveau type
                    </Button>
                  </li>
                  <li>
                    <Button color="primary" size="md" onClick={openNew}>
                      <Icon name="plus" />
                      <span>Nouvel actif</span>
                    </Button>
                  </li>
                </PermissionGate>
              </ul>
            </BlockHeadContent>
          </BlockBetween>
          {loading ? (
            <div className="card card-stretch">
              <div className="card-inner text-center py-5">
                <Spinner />
              </div>
            </div>
          ) : filteredAssets.length === 0 ? (
            <div className="card card-stretch">
              <div className="card-inner text-center py-5">
                <p className="text-soft mb-0">
                  {listSearch ? "Aucun actif ne correspond à la recherche" : "Aucun actif"}
                </p>
              </div>
            </div>
          ) : (
            <>
              <DataTable className="card-stretch">
                <DataTableBody>
                  <DataTableHead className="nk-tb-item nk-tb-head">
                    <DataTableRow size="xl">
                      <span className="sub-text">Nom</span>
                    </DataTableRow>
                    <DataTableRow size="lg">
                      <span className="sub-text">Site</span>
                    </DataTableRow>
                    <DataTableRow size="md">
                      <span className="sub-text">Type</span>
                    </DataTableRow>
                    <DataTableRow size="sm">
                      <span className="sub-text">Score risque</span>
                    </DataTableRow>
                    <DataTableRow className="nk-tb-col-tools text-end">
                      <span className="sub-text">Actions</span>
                    </DataTableRow>
                  </DataTableHead>
                  {pageAssets.map((a) => (
                    <DataTableItem key={a.id}>
                      <DataTableRow size="xl">
                        <span className="tb-lead">{a.name}</span>
                      </DataTableRow>
                      <DataTableRow size="lg">
                        <span className="tb-sub">{a.site?.name || a.site_id}</span>
                      </DataTableRow>
                      <DataTableRow size="md">
                        <span className="tb-sub">{a.asset_type?.name || a.asset_type_id}</span>
                      </DataTableRow>
                      <DataTableRow size="sm">
                        <span className="tb-sub">{a.latest_risk_score?.score ?? a.latestRiskScore?.score ?? "—"}</span>
                      </DataTableRow>
                      <DataTableRow className="nk-tb-col-tools">
                        <ul className="nk-tb-actions gx-1">
                          <li>
                            <button
                              type="button"
                              className="btn btn-trigger btn-icon"
                              title="Détail"
                              onClick={() => openDetail(a)}
                            >
                              <Icon name="eye" />
                            </button>
                          </li>
                          <PermissionGate permission={["Actifs - Modifier"]}>
                            <li>
                              <button
                                type="button"
                                className="btn btn-trigger btn-icon"
                                title="Modifier"
                                onClick={() => openEdit(a)}
                              >
                                <Icon name="edit" />
                              </button>
                            </li>
                          </PermissionGate>
                          <PermissionGate permission={["Actifs - Supprimer"]}>
                            <li>
                              <button
                                type="button"
                                className="btn btn-trigger btn-icon text-danger"
                                title="Supprimer"
                                onClick={() => {
                                  setDeleteId(a.id);
                                  setDeleteModal(true);
                                }}
                              >
                                <Icon name="trash" />
                              </button>
                            </li>
                          </PermissionGate>
                        </ul>
                      </DataTableRow>
                    </DataTableItem>
                  ))}
                </DataTableBody>
              </DataTable>
              <div className="card-inner">
                <PaginationComponent
                  itemPerPage={itemPerPage}
                  totalItems={filteredAssets.length}
                  paginate={setListPage}
                  currentPage={listPage}
                />
              </div>
            </>
          )}
        </Block>

        <Modal isOpen={modal} toggle={() => setModal(false)}>
          <div className="modal-header">
            <h5 className="modal-title">{editId ? "Modifier l’actif" : "Nouvel actif"}</h5>
            <button type="button" className="close" onClick={() => setModal(false)}>
              <Icon name="cross" />
            </button>
          </div>
          <ModalBody>
            <Form className="is-alter" onSubmit={handleSubmit(onSave)}>
              <div className="form-group">
                <label className="form-label">Site *</label>
                <select
                  className="form-select"
                  name="site_id"
                  ref={register({ required: !editId })}
                  disabled={!!editId}
                >
                  <option value="">—</option>
                  {sites.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
                {editId && (
                  <p className="text-soft small mt-1">Le site ne peut pas être modifié via l’API (créer un nouvel actif si besoin).</p>
                )}
              </div>
              <div className="form-group">
                <label className="form-label">Type *</label>
                <select
                  className="form-select"
                  name="asset_type_id"
                  ref={register({ required: !editId })}
                  disabled={!!editId}
                >
                  <option value="">—</option>
                  {types.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
                {editId && (
                  <p className="text-soft small mt-1">Le type d’actif n’est pas modifiable après création.</p>
                )}
              </div>
              <div className="form-group">
                <label className="form-label">Nom *</label>
                <input className="form-control" name="name" ref={register({ required: true })} />
              </div>
              <div className="form-group">
                <label className="form-label">Identifiant</label>
                <input className="form-control" name="identifier" ref={register} />
              </div>
              <div className="custom-control custom-checkbox mb-3">
                <input type="checkbox" className="custom-control-input" id="msf" name="maintenance_safety_flag" ref={register} />
                <label className="custom-control-label" htmlFor="msf">
                  Alerte maintenance / sécurité
                </label>
              </div>
              <div className="text-end">
                <Button color="primary" type="submit">
                  Enregistrer
                </Button>
              </div>
            </Form>
          </ModalBody>
        </Modal>

        <Modal isOpen={typeModal} toggle={() => setTypeModal(false)}>
          <ModalBody>
            <h5 className="mb-3">Nouveau type d’actif</h5>
            <Form className="is-alter" onSubmit={typeForm.handleSubmit(onNewType)}>
              <input className="form-control mb-2" name="type_name" placeholder="Nom" ref={typeForm.register({ required: true })} />
              <input className="form-control mb-2" name="type_slug" placeholder="Slug (optionnel)" ref={typeForm.register} />
              <Button color="primary" type="submit">
                Créer
              </Button>
            </Form>
          </ModalBody>
        </Modal>

        <Modal isOpen={!!detail} toggle={() => setDetail(null)} className="modal-lg">
          <div className="modal-header">
            <h5 className="modal-title">{detail?.name}</h5>
            <button type="button" className="close" onClick={() => setDetail(null)}>
              <Icon name="cross" />
            </button>
          </div>
          <ModalBody>
            {detail && (
              <>
                <p className="text-soft small">
                  {detail.site?.name} — {detail.asset_type?.name}
                </p>
                <PermissionGate permission={["Actifs - Voir"]}>
                  <Button
                    color="light"
                    size="sm"
                    className="me-2 mb-3"
                    onClick={async () => {
                      try {
                        const r = await api.postAssetRiskScore(detail.id);
                        const scoreVal = r?.score ?? r?.data?.score;
                        toast.success(
                          scoreVal != null ? `Score recalculé : ${scoreVal}` : "Score recalculé"
                        );
                        setDetail(await api.getAsset(detail.id));
                        load();
                      } catch (e) {
                        toast.error(e.message || "Erreur");
                      }
                    }}
                  >
                    Recalculer le score
                  </Button>
                </PermissionGate>
                <h6>Zones de risque liées</h6>
                <ul className="list list-sm">
                  {(detail.risk_zones || detail.riskZones || []).map((z) => (
                    <li key={z.id}>{z.name}</li>
                  ))}
                </ul>
                <div className="form-group">
                  <label className="form-label">Lier une zone</label>
                  <select
                    className="form-select"
                    defaultValue=""
                    onChange={async (e) => {
                      const v = e.target.value;
                      if (!v) return;
                      try {
                        await api.linkAssetRiskZone(detail.id, parseInt(v, 10));
                        toast.success("Zone liée");
                        setDetail(await api.getAsset(detail.id));
                      } catch (err) {
                        toast.error(err.message || "Erreur");
                      }
                      e.target.value = "";
                    }}
                  >
                    <option value="">— Choisir —</option>
                    {riskZones.map((z) => (
                      <option key={z.id} value={z.id}>
                        {z.name}
                      </option>
                    ))}
                  </select>
                </div>
                <h6 className="mt-3">Historique sécurité</h6>
                {!safetyHistory?.timeline?.length ? (
                  <p className="text-soft small">Aucun incident ou inspection lié récemment.</p>
                ) : (
                  <ul className="list list-sm">
                    {safetyHistory.timeline.slice(0, 25).map((ev, i) => (
                      <li key={`${ev.type}-${ev.id}-${i}`}>
                        <span className="badge badge-dim bg-outline-secondary me-1">{ev.type}</span>
                        {ev.type === "inspection" && (
                          <>
                            Inspection #{ev.id} — {ev.status}
                            {ev.score_percent != null ? ` — ${ev.score_percent}%` : ""}
                          </>
                        )}
                        {ev.type === "incident" && (
                          <>
                            {ev.title} — {ev.severity} — {ev.status}
                          </>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </ModalBody>
        </Modal>

        <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)}>
          <ModalBody>
            <p>Supprimer cet actif ?</p>
            <div className="text-end">
              <Button color="light" className="me-2" onClick={() => setDeleteModal(false)}>
                Annuler
              </Button>
              <Button color="danger" onClick={onDelete}>
                Supprimer
              </Button>
            </div>
          </ModalBody>
        </Modal>
      </Content>
    </React.Fragment>
  );
};

export default HseProcessAssetsPage;
